// ─── Feuille : créer / éditer une RECETTE ─────────────────────────
// Ingrédients et étapes s'éditent EN LIGNE dans cette même feuille (jamais un
// second <Modal> imbriqué — piège connu, cf. AGENTS.md).
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet } from 'react-native';
import { Feuille, ChoixPuces, Stepper, stylesForm, useUI, useT } from '@mop/core';

const VIDE = { nom: '', categorie: '', portions: 4, tempsMin: '', ingredients: [], etapes: [] };

export default function FeuilleRecette({ visible, initial, categories = [], catalogue = [], onEnregistrer, onFermer }) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const [f, setF] = useState(VIDE);

  useEffect(() => {
    if (!visible) return;
    const r = initial || {};
    setF({
      ...VIDE, ...r,
      portions: Math.max(1, Number(r.portions) || 4),
      tempsMin: r.tempsMin != null ? String(r.tempsMin) : '',
      ingredients: r.ingredients || [],
      etapes: r.etapes || [],
    });
  }, [visible, initial]);

  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const valide = !!(f.nom || '').trim();

  const setIng = (i, k, v) => setF(x => ({ ...x, ingredients: x.ingredients.map((ing, j) => j === i ? { ...ing, [k]: v } : ing) }));
  const ajouterIng = () => setF(x => ({ ...x, ingredients: [...x.ingredients, { nom: '', quantite: '', unite: '', prix: '' }] }));
  const retirerIng = (i) => setF(x => ({ ...x, ingredients: x.ingredients.filter((_, j) => j !== i) }));
  const choisirSuggestion = (i, item) => setF(x => ({
    ...x, ingredients: x.ingredients.map((ing, j) => j === i ? { ...ing, nom: item.nom, unite: item.unite || ing.unite } : ing),
  }));
  // Suggestions du référentiel : nom contient le texte tapé, hors correspondance exacte.
  const suggestionsIng = (texte) => {
    const q = (texte || '').trim().toLowerCase();
    if (!q) return [];
    return catalogue.filter(c => c.nom.toLowerCase().includes(q) && c.nom.toLowerCase() !== q).slice(0, 5);
  };

  const setEtape = (i, v) => setF(x => ({ ...x, etapes: x.etapes.map((e, j) => j === i ? v : e) }));
  const ajouterEtape = () => setF(x => ({ ...x, etapes: [...x.etapes, ''] }));
  const retirerEtape = (i) => setF(x => ({ ...x, etapes: x.etapes.filter((_, j) => j !== i) }));

  return (
    <Feuille visible={visible} titre={initial?.id ? t('repas.modifier') : t('repas.nouvelle')}
      emoji="🍽️" hauteur={0.9} onFermer={onFermer}
      action={{ icone: initial?.id ? '💾' : '＋', label: t('commun.enregistrer'), disabled: !valide, onPress: () => onEnregistrer(f) }}>

      <Text style={s.lbl}>{t('commun.nom')}</Text>
      <TextInput style={s.input} value={f.nom} onChangeText={v => set('nom', v)}
        placeholder={t('repas.phNom')} placeholderTextColor={UI.faint} />

      {categories.length > 0 && (
        <>
          <Text style={s.lbl}>{t('repas.choisirCategorie')}</Text>
          <ChoixPuces options={categories.map(c => ({ id: c.id, label: `${c.emoji} ${c.label}` }))}
            value={f.categorie} onChange={v => set('categorie', v)} />
        </>
      )}

      <View style={s.row}>
        <View style={{ flex: 1 }}>
          <Text style={s.lbl}>{t('repas.portions')}</Text>
          <Stepper valeur={f.portions} onMoins={() => set('portions', Math.max(1, f.portions - 1))} onPlus={() => set('portions', f.portions + 1)} />
        </View>
        <View style={{ flex: 1 }}>
          <Text style={s.lbl}>{t('repas.temps')}</Text>
          <View style={s.tempsRow}>
            <TextInput style={[s.input, { flex: 1 }]} value={f.tempsMin} onChangeText={v => set('tempsMin', v.replace(/\D/g, ''))}
              keyboardType="number-pad" placeholder="0" placeholderTextColor={UI.faint} />
            <Text style={s.min}>{t('repas.minutes')}</Text>
          </View>
        </View>
      </View>

      <Text style={s.lbl}>{t('repas.ingredients')}</Text>
      {f.ingredients.map((ing, i) => {
        const suggestions = suggestionsIng(ing.nom);
        return (
          <View key={i} style={{ marginBottom: 6 }}>
            <View style={s.ligneIng}>
              <TextInput style={[s.input, { flex: 3 }]} value={ing.nom} onChangeText={v => setIng(i, 'nom', v)}
                placeholder={t('repas.phIngredient')} placeholderTextColor={UI.faint} />
              <TextInput style={[s.input, { flex: 1 }]} value={ing.quantite} onChangeText={v => setIng(i, 'quantite', v)}
                placeholder={t('repas.qte')} placeholderTextColor={UI.faint} />
              <TextInput style={[s.input, { flex: 1 }]} value={ing.unite} onChangeText={v => setIng(i, 'unite', v)}
                placeholder={t('repas.unite')} placeholderTextColor={UI.faint} />
              <TextInput style={[s.input, { flex: 1 }]} value={ing.prix != null ? String(ing.prix) : ''}
                onChangeText={v => setIng(i, 'prix', v.replace(/[^0-9.,]/g, ''))}
                keyboardType="numbers-and-punctuation" placeholder={t('repas.prix')} placeholderTextColor={UI.faint} />
              <TouchableOpacity style={s.retirer} onPress={() => retirerIng(i)}><Text style={s.retirerTxt}>✕</Text></TouchableOpacity>
            </View>
            {suggestions.length > 0 && (
              <View style={s.suggestions}>
                {suggestions.map(sg => (
                  <TouchableOpacity key={sg.id} style={s.chip} onPress={() => choisirSuggestion(i, sg)}>
                    <Text style={s.chipTxt}>{sg.nom}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            )}
          </View>
        );
      })}
      <TouchableOpacity style={s.ajouter} onPress={ajouterIng}>
        <Text style={s.ajouterTxt}>＋ {t('repas.ajouterIngredient')}</Text>
      </TouchableOpacity>

      <Text style={s.lbl}>{t('repas.preparation')}</Text>
      {f.etapes.map((e, i) => (
        <View key={i} style={s.ligneEtape}>
          <Text style={s.numEtape}>{i + 1}.</Text>
          <TextInput style={[s.input, { flex: 1 }]} value={e} onChangeText={v => setEtape(i, v)}
            placeholder={t('repas.phEtape')} placeholderTextColor={UI.faint} multiline />
          <TouchableOpacity style={s.retirer} onPress={() => retirerEtape(i)}><Text style={s.retirerTxt}>✕</Text></TouchableOpacity>
        </View>
      ))}
      <TouchableOpacity style={s.ajouter} onPress={ajouterEtape}>
        <Text style={s.ajouterTxt}>＋ {t('repas.ajouterEtape')}</Text>
      </TouchableOpacity>
    </Feuille>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  ...stylesForm(UI),
  row: { flexDirection: 'row', gap: 8 },
  tempsRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  min: { fontSize: 13, color: UI.muted },
  ligneIng: { flexDirection: 'row', gap: 6, alignItems: 'center' },
  suggestions: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 4 },
  chip: { paddingHorizontal: 10, paddingVertical: 5, borderRadius: 999, backgroundColor: UI.accentTint, borderWidth: 0.5, borderColor: UI.accent },
  chipTxt: { fontSize: 12, fontWeight: '700', color: UI.accent },
  ligneEtape: { flexDirection: 'row', gap: 6, marginBottom: 6, alignItems: 'flex-start' },
  numEtape: { fontSize: 14, fontWeight: '800', color: UI.muted, paddingTop: 10, width: 18 },
  retirer: { width: 30, height: 30, borderRadius: 8, alignItems: 'center', justifyContent: 'center' },
  retirerTxt: { fontSize: 16, color: UI.danger || '#c0392b' },
  ajouter: { paddingVertical: 9, alignItems: 'center', marginBottom: 14 },
  ajouterTxt: { fontSize: 13.5, fontWeight: '700', color: UI.accent },
});
